import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  Animation,
  PropertyTrack,
  Keyframe,
  PropertyType,
  EasingType,
  OnionSkinSettings,
  TimelineViewport,
  PlaybackState,
  ViewMode,
  DEFAULT_FPS,
  DEFAULT_DURATION,
  DEFAULT_ONION_SKIN,
  PROPERTY_COLORS,
  BezierHandle,
} from '../../core/types/animation.types';
import { applyEasing } from '../utils/easing';

const generateId = () => Math.random().toString(36).substring(2, 11);

interface AnimationState {
  // Animations
  animations: Record<string, Animation>;
  selectedAnimationId: string | null;

  // Selection
  selectedTrackIds: string[];
  selectedKeyframeIds: string[];

  // Clipboard
  clipboardKeyframes: Keyframe[];
  clipboardTrackId: string | null;

  // View
  viewMode: ViewMode;
  viewport: TimelineViewport;
  showCurveEditor: boolean;
  curveEditorHeight: number;

  // Playback
  playback: PlaybackState;

  // Onion skinning
  onionSkin: OnionSkinSettings;

  // UI State
  isRecording: boolean;
  snapToFrames: boolean;
  autoKeyframe: boolean;
  showGrid: boolean;
  gridSubdivisions: number;
}

interface AnimationActions {
  // Animation CRUD
  createAnimation: (name?: string) => string;
  deleteAnimation: (id: string) => void;
  duplicateAnimation: (id: string) => string;
  updateAnimation: (id: string, updates: Partial<Animation>) => void;
  selectAnimation: (id: string | null) => void;

  // Track operations
  addTrack: (animationId: string, property: PropertyType, targetObjectId: string) => string;
  removeTrack: (animationId: string, trackId: string) => void;
  updateTrack: (animationId: string, trackId: string, updates: Partial<PropertyTrack>) => void;
  selectTrack: (trackId: string, addToSelection?: boolean) => void;
  clearTrackSelection: () => void;
  toggleTrackVisibility: (animationId: string, trackId: string) => void;
  toggleTrackLock: (animationId: string, trackId: string) => void;

  // Keyframe operations
  addKeyframe: (animationId: string, trackId: string, time: number, value: number | string) => string;
  removeKeyframe: (animationId: string, trackId: string, keyframeId: string) => void;
  updateKeyframe: (animationId: string, trackId: string, keyframeId: string, updates: Partial<Keyframe>) => void;
  moveKeyframe: (animationId: string, trackId: string, keyframeId: string, newTime: number) => void;
  selectKeyframe: (keyframeId: string, addToSelection?: boolean) => void;
  selectKeyframesInRange: (startTime: number, endTime: number, trackIds?: string[]) => void;
  clearKeyframeSelection: () => void;
  copyKeyframes: () => void;
  pasteKeyframes: (targetTime: number) => void;
  deleteSelectedKeyframes: () => void;

  // Easing
  setKeyframeEasing: (animationId: string, trackId: string, keyframeId: string, easing: EasingType) => void;
  setKeyframeBezier: (animationId: string, trackId: string, keyframeId: string, handles: { in: BezierHandle; out: BezierHandle }) => void;

  // Playback
  play: () => void;
  pause: () => void;
  stop: () => void;
  setCurrentTime: (time: number) => void;
  stepForward: () => void;
  stepBackward: () => void;
  goToStart: () => void;
  goToEnd: () => void;
  setFps: (fps: number) => void;

  // Viewport
  setViewMode: (mode: ViewMode) => void;
  setViewport: (viewport: Partial<TimelineViewport>) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  fitToView: () => void;

  // Onion skin
  setOnionSkin: (settings: Partial<OnionSkinSettings>) => void;
  toggleOnionSkin: () => void;

  // UI Settings
  setRecording: (recording: boolean) => void;
  toggleSnapToFrames: () => void;
  toggleAutoKeyframe: () => void;
  toggleGrid: () => void;
  setGridSubdivisions: (subdivisions: number) => void;
  toggleCurveEditor: () => void;
  setCurveEditorHeight: (height: number) => void;

  // Utility
  getAnimation: (id: string) => Animation | undefined;
  getTrack: (animationId: string, trackId: string) => PropertyTrack | undefined;
  getKeyframe: (animationId: string, trackId: string, keyframeId: string) => Keyframe | undefined;
  getValueAtTime: (animationId: string, trackId: string, time: number) => number | string | undefined;
}

const initialViewport: TimelineViewport = {
  startTime: 0,
  endTime: 5,
  pixelsPerSecond: 100,
  scrollLeft: 0,
  scrollTop: 0,
};

const initialPlayback: PlaybackState = {
  isPlaying: false,
  isPaused: false,
  currentTime: 0,
  currentFrame: 0,
  fps: DEFAULT_FPS,
};

type AnimationStore = AnimationState & AnimationActions;

export const useAnimationStore = create<AnimationStore>()(
  immer((set, get) => ({
      // Initial state
      animations: {},
      selectedAnimationId: null,
      selectedTrackIds: [],
      selectedKeyframeIds: [],
      clipboardKeyframes: [],
      clipboardTrackId: null,
      viewMode: 'timeline',
      viewport: initialViewport,
      showCurveEditor: false,
      curveEditorHeight: 200,
      playback: initialPlayback,
      onionSkin: DEFAULT_ONION_SKIN,
      isRecording: false,
      snapToFrames: true,
      autoKeyframe: false,
      showGrid: true,
      gridSubdivisions: 4,

      // Animation CRUD
      createAnimation: (name = 'New Animation') => {
        const id = generateId();

        // Create demo tracks with keyframes so users can test the curve editor
        const demoObjectId = 'demo-object';
        const demoTracks: PropertyTrack[] = [
          {
            id: generateId(),
            property: 'position.x',
            targetObjectId: demoObjectId,
            keyframes: [
              { id: generateId(), time: 0, value: 0, easing: 'ease-in-out-quad' },
              { id: generateId(), time: 1, value: 200, easing: 'ease-in-out-quad' },
              { id: generateId(), time: 2, value: 0, easing: 'ease-in-out-quad' },
            ],
            visible: true,
            locked: false,
            color: PROPERTY_COLORS['position.x'],
            expanded: true,
          },
          {
            id: generateId(),
            property: 'position.y',
            targetObjectId: demoObjectId,
            keyframes: [
              { id: generateId(), time: 0, value: 0, easing: 'linear' },
              { id: generateId(), time: 0.5, value: -100, easing: 'ease-out-bounce' },
              { id: generateId(), time: 1.5, value: -100, easing: 'ease-in-quad' },
              { id: generateId(), time: 2, value: 0, easing: 'linear' },
            ],
            visible: true,
            locked: false,
            color: PROPERTY_COLORS['position.y'],
            expanded: true,
          },
          {
            id: generateId(),
            property: 'alpha',
            targetObjectId: demoObjectId,
            keyframes: [
              { id: generateId(), time: 0, value: 1, easing: 'linear' },
              { id: generateId(), time: 1, value: 0.5, easing: 'ease-in-out-sine' },
              { id: generateId(), time: 2, value: 1, easing: 'linear' },
            ],
            visible: true,
            locked: false,
            color: PROPERTY_COLORS['alpha'],
            expanded: true,
          },
        ];

        const animation: Animation = {
          id,
          name,
          duration: DEFAULT_DURATION,
          loop: true,
          loopCount: 0,
          speed: 1,
          tracks: demoTracks,
          createdAt: new Date(),
          modifiedAt: new Date(),
        };

        set((state) => {
          state.animations[id] = animation;
          state.selectedAnimationId = id;
          // Auto-select the first keyframe so curve editor works immediately
          if (demoTracks.length > 0 && demoTracks[0].keyframes.length > 0) {
            state.selectedKeyframeIds = [demoTracks[0].keyframes[0].id];
          }
        });

        return id;
      },

      deleteAnimation: (id) => {
        set((state) => {
          delete state.animations[id];
          if (state.selectedAnimationId === id) {
            const remaining = Object.keys(state.animations);
            state.selectedAnimationId = remaining.length > 0 ? remaining[0] : null;
          }
        });
      },

      duplicateAnimation: (id) => {
        const original = get().animations[id];
        if (!original) return '';

        const newId = generateId();
        const duplicate: Animation = {
          ...original,
          id: newId,
          name: `${original.name} (Copy)`,
          tracks: original.tracks.map((track) => ({
            ...track,
            id: generateId(),
            keyframes: track.keyframes.map((kf) => ({ ...kf, id: generateId() })),
          })),
          createdAt: new Date(),
          modifiedAt: new Date(),
        };

        set((state) => {
          state.animations[newId] = duplicate;
          state.selectedAnimationId = newId;
        });

        return newId;
      },

      updateAnimation: (id, updates) => {
        set((state) => {
          if (state.animations[id]) {
            Object.assign(state.animations[id], updates);
            state.animations[id].modifiedAt = new Date();
          }
        });
      },

      selectAnimation: (id) => {
        set((state) => {
          state.selectedAnimationId = id;
          state.selectedTrackIds = [];
          state.selectedKeyframeIds = [];
          state.playback.currentTime = 0;
          state.playback.currentFrame = 0;
        });
      },

      // Track operations
      addTrack: (animationId, property, targetObjectId) => {
        const trackId = generateId();
        const track: PropertyTrack = {
          id: trackId,
          property,
          targetObjectId,
          keyframes: [],
          visible: true,
          locked: false,
          color: PROPERTY_COLORS[property],
          expanded: true,
        };

        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            animation.tracks.push(track);
            animation.modifiedAt = new Date();
          }
        });

        return trackId;
      },

      removeTrack: (animationId, trackId) => {
        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            animation.tracks = animation.tracks.filter((t) => t.id !== trackId);
            animation.modifiedAt = new Date();
            state.selectedTrackIds = state.selectedTrackIds.filter((id) => id !== trackId);
          }
        });
      },

      updateTrack: (animationId, trackId, updates) => {
        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            const track = animation.tracks.find((t) => t.id === trackId);
            if (track) {
              Object.assign(track, updates);
              animation.modifiedAt = new Date();
            }
          }
        });
      },

      selectTrack: (trackId, addToSelection = false) => {
        set((state) => {
          if (addToSelection) {
            if (state.selectedTrackIds.includes(trackId)) {
              state.selectedTrackIds = state.selectedTrackIds.filter((id) => id !== trackId);
            } else {
              state.selectedTrackIds.push(trackId);
            }
          } else {
            state.selectedTrackIds = [trackId];
          }
        });
      },

      clearTrackSelection: () => {
        set((state) => {
          state.selectedTrackIds = [];
        });
      },

      toggleTrackVisibility: (animationId, trackId) => {
        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            const track = animation.tracks.find((t) => t.id === trackId);
            if (track) {
              track.visible = !track.visible;
            }
          }
        });
      },

      toggleTrackLock: (animationId, trackId) => {
        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            const track = animation.tracks.find((t) => t.id === trackId);
            if (track) {
              track.locked = !track.locked;
            }
          }
        });
      },

      // Keyframe operations
      addKeyframe: (animationId, trackId, time, value) => {
        const keyframeId = generateId();
        const { snapToFrames, playback } = get();

        // Snap to frame if enabled
        let snappedTime = time;
        if (snapToFrames) {
          const frameTime = 1 / playback.fps;
          snappedTime = Math.round(time / frameTime) * frameTime;
        }

        const keyframe: Keyframe = {
          id: keyframeId,
          time: snappedTime,
          value,
          easing: 'ease-out',
        };

        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            const track = animation.tracks.find((t) => t.id === trackId);
            if (track && !track.locked) {
              // Remove existing keyframe at same time
              track.keyframes = track.keyframes.filter(
                (kf) => Math.abs(kf.time - snappedTime) > 0.001
              );
              track.keyframes.push(keyframe);
              // Sort by time
              track.keyframes.sort((a, b) => a.time - b.time);
              animation.modifiedAt = new Date();
            }
          }
        });

        return keyframeId;
      },

      removeKeyframe: (animationId, trackId, keyframeId) => {
        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            const track = animation.tracks.find((t) => t.id === trackId);
            if (track && !track.locked) {
              track.keyframes = track.keyframes.filter((kf) => kf.id !== keyframeId);
              animation.modifiedAt = new Date();
            }
          }
          state.selectedKeyframeIds = state.selectedKeyframeIds.filter((id) => id !== keyframeId);
        });
      },

      updateKeyframe: (animationId, trackId, keyframeId, updates) => {
        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            const track = animation.tracks.find((t) => t.id === trackId);
            if (track) {
              const keyframe = track.keyframes.find((kf) => kf.id === keyframeId);
              if (keyframe) {
                Object.assign(keyframe, updates);
                animation.modifiedAt = new Date();
              }
            }
          }
        });
      },

      moveKeyframe: (animationId, trackId, keyframeId, newTime) => {
        const { snapToFrames, playback } = get();

        let snappedTime = newTime;
        if (snapToFrames) {
          const frameTime = 1 / playback.fps;
          snappedTime = Math.round(newTime / frameTime) * frameTime;
        }

        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            const track = animation.tracks.find((t) => t.id === trackId);
            if (track && !track.locked) {
              const keyframe = track.keyframes.find((kf) => kf.id === keyframeId);
              if (keyframe) {
                keyframe.time = Math.max(0, Math.min(snappedTime, animation.duration));
                track.keyframes.sort((a, b) => a.time - b.time);
                animation.modifiedAt = new Date();
              }
            }
          }
        });
      },

      selectKeyframe: (keyframeId, addToSelection = false) => {
        set((state) => {
          if (addToSelection) {
            if (state.selectedKeyframeIds.includes(keyframeId)) {
              state.selectedKeyframeIds = state.selectedKeyframeIds.filter((id) => id !== keyframeId);
            } else {
              state.selectedKeyframeIds.push(keyframeId);
            }
          } else {
            state.selectedKeyframeIds = [keyframeId];
          }
        });
      },

      selectKeyframesInRange: (startTime, endTime, trackIds) => {
        set((state) => {
          const animation = state.animations[state.selectedAnimationId || ''];
          if (!animation) return;

          const keyframeIds: string[] = [];
          animation.tracks.forEach((track) => {
            if (trackIds && !trackIds.includes(track.id)) return;
            track.keyframes.forEach((kf) => {
              if (kf.time >= startTime && kf.time <= endTime) {
                keyframeIds.push(kf.id);
              }
            });
          });

          state.selectedKeyframeIds = keyframeIds;
        });
      },

      clearKeyframeSelection: () => {
        set((state) => {
          state.selectedKeyframeIds = [];
        });
      },

      copyKeyframes: () => {
        const state = get();
        const animation = state.animations[state.selectedAnimationId || ''];
        if (!animation) return;

        const keyframes: Keyframe[] = [];
        let trackId: string | null = null;

        animation.tracks.forEach((track) => {
          track.keyframes.forEach((kf) => {
            if (state.selectedKeyframeIds.includes(kf.id)) {
              keyframes.push({ ...kf });
              trackId = track.id;
            }
          });
        });

        set((s) => {
          s.clipboardKeyframes = keyframes;
          s.clipboardTrackId = trackId;
        });
      },

      pasteKeyframes: (targetTime) => {
        const state = get();
        if (state.clipboardKeyframes.length === 0 || !state.selectedAnimationId) return;

        const minTime = Math.min(...state.clipboardKeyframes.map((kf) => kf.time));

        set((s) => {
          const animation = s.animations[s.selectedAnimationId || ''];
          if (!animation) return;

          // Paste to selected track or original track
          const targetTrackId = s.selectedTrackIds[0] || s.clipboardTrackId;
          const track = animation.tracks.find((t) => t.id === targetTrackId);

          if (track && !track.locked) {
            const newKeyframes = s.clipboardKeyframes.map((kf) => ({
              ...kf,
              id: generateId(),
              time: kf.time - minTime + targetTime,
            }));

            track.keyframes.push(...newKeyframes);
            track.keyframes.sort((a, b) => a.time - b.time);
            animation.modifiedAt = new Date();

            s.selectedKeyframeIds = newKeyframes.map((kf) => kf.id);
          }
        });
      },

      deleteSelectedKeyframes: () => {
        set((state) => {
          const animation = state.animations[state.selectedAnimationId || ''];
          if (!animation) return;

          animation.tracks.forEach((track) => {
            if (!track.locked) {
              track.keyframes = track.keyframes.filter(
                (kf) => !state.selectedKeyframeIds.includes(kf.id)
              );
            }
          });

          animation.modifiedAt = new Date();
          state.selectedKeyframeIds = [];
        });
      },

      // Easing
      setKeyframeEasing: (animationId, trackId, keyframeId, easing) => {
        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            const track = animation.tracks.find((t) => t.id === trackId);
            if (track) {
              const keyframe = track.keyframes.find((kf) => kf.id === keyframeId);
              if (keyframe) {
                keyframe.easing = easing;
                if (easing !== 'custom') {
                  delete keyframe.bezierHandles;
                }
                animation.modifiedAt = new Date();
              }
            }
          }
        });
      },

      setKeyframeBezier: (animationId, trackId, keyframeId, handles) => {
        set((state) => {
          const animation = state.animations[animationId];
          if (animation) {
            const track = animation.tracks.find((t) => t.id === trackId);
            if (track) {
              const keyframe = track.keyframes.find((kf) => kf.id === keyframeId);
              if (keyframe) {
                keyframe.easing = 'custom';
                keyframe.bezierHandles = handles;
                animation.modifiedAt = new Date();
              }
            }
          }
        });
      },

      // Playback
      play: () => {
        set((state) => {
          state.playback.isPlaying = true;
          state.playback.isPaused = false;
        });
      },

      pause: () => {
        set((state) => {
          state.playback.isPlaying = false;
          state.playback.isPaused = true;
        });
      },

      stop: () => {
        set((state) => {
          state.playback.isPlaying = false;
          state.playback.isPaused = false;
          state.playback.currentTime = 0;
          state.playback.currentFrame = 0;
        });
      },

      setCurrentTime: (time) => {
        set((state) => {
          const animation = state.animations[state.selectedAnimationId || ''];
          const duration = animation?.duration || DEFAULT_DURATION;
          state.playback.currentTime = Math.max(0, Math.min(time, duration));
          state.playback.currentFrame = Math.round(state.playback.currentTime * state.playback.fps);
        });
      },

      stepForward: () => {
        const { playback, selectedAnimationId, animations } = get();
        const animation = animations[selectedAnimationId || ''];
        const frameTime = 1 / playback.fps;
        const newTime = Math.min(playback.currentTime + frameTime, animation?.duration || DEFAULT_DURATION);
        get().setCurrentTime(newTime);
      },

      stepBackward: () => {
        const { playback } = get();
        const frameTime = 1 / playback.fps;
        const newTime = Math.max(playback.currentTime - frameTime, 0);
        get().setCurrentTime(newTime);
      },

      goToStart: () => {
        get().setCurrentTime(0);
      },

      goToEnd: () => {
        const { selectedAnimationId, animations } = get();
        const animation = animations[selectedAnimationId || ''];
        get().setCurrentTime(animation?.duration || DEFAULT_DURATION);
      },

      setFps: (fps) => {
        set((state) => {
          state.playback.fps = Math.max(1, Math.min(120, fps));
        });
      },

      // Viewport
      setViewMode: (mode) => {
        set((state) => {
          state.viewMode = mode;
        });
      },

      setViewport: (viewport) => {
        set((state) => {
          Object.assign(state.viewport, viewport);
        });
      },

      zoomIn: () => {
        set((state) => {
          state.viewport.pixelsPerSecond = Math.min(state.viewport.pixelsPerSecond * 1.5, 500);
        });
      },

      zoomOut: () => {
        set((state) => {
          state.viewport.pixelsPerSecond = Math.max(state.viewport.pixelsPerSecond / 1.5, 20);
        });
      },

      fitToView: () => {
        const { selectedAnimationId, animations } = get();
        const animation = animations[selectedAnimationId || ''];
        if (!animation) return;

        set((state) => {
          state.viewport.startTime = 0;
          state.viewport.endTime = animation.duration;
          state.viewport.scrollLeft = 0;
        });
      },

      // Onion skin
      setOnionSkin: (settings) => {
        set((state) => {
          Object.assign(state.onionSkin, settings);
        });
      },

      toggleOnionSkin: () => {
        set((state) => {
          state.onionSkin.enabled = !state.onionSkin.enabled;
        });
      },

      // UI Settings
      setRecording: (recording) => {
        set((state) => {
          state.isRecording = recording;
        });
      },

      toggleSnapToFrames: () => {
        set((state) => {
          state.snapToFrames = !state.snapToFrames;
        });
      },

      toggleAutoKeyframe: () => {
        set((state) => {
          state.autoKeyframe = !state.autoKeyframe;
        });
      },

      toggleGrid: () => {
        set((state) => {
          state.showGrid = !state.showGrid;
        });
      },

      setGridSubdivisions: (subdivisions) => {
        set((state) => {
          state.gridSubdivisions = Math.max(1, Math.min(16, subdivisions));
        });
      },

      toggleCurveEditor: () => {
        set((state) => {
          state.showCurveEditor = !state.showCurveEditor;
        });
      },

      setCurveEditorHeight: (height) => {
        set((state) => {
          state.curveEditorHeight = Math.max(100, Math.min(500, height));
        });
      },

      // Utility
      getAnimation: (id) => get().animations[id],

      getTrack: (animationId, trackId) => {
        const animation = get().animations[animationId];
        return animation?.tracks.find((t) => t.id === trackId);
      },

      getKeyframe: (animationId, trackId, keyframeId) => {
        const track = get().getTrack(animationId, trackId);
        return track?.keyframes.find((kf) => kf.id === keyframeId);
      },

      getValueAtTime: (animationId, trackId, time) => {
        const track = get().getTrack(animationId, trackId);
        if (!track || track.keyframes.length === 0) return undefined;

        // Find surrounding keyframes
        const keyframes = track.keyframes;

        // Before first keyframe
        if (time <= keyframes[0].time) {
          return keyframes[0].value;
        }

        // After last keyframe
        if (time >= keyframes[keyframes.length - 1].time) {
          return keyframes[keyframes.length - 1].value;
        }

        // Find the two surrounding keyframes
        for (let i = 0; i < keyframes.length - 1; i++) {
          const kf1 = keyframes[i];
          const kf2 = keyframes[i + 1];

          if (time >= kf1.time && time <= kf2.time) {
            // Interpolate based on easing
            const t = (time - kf1.time) / (kf2.time - kf1.time);

            if (typeof kf1.value === 'string' || typeof kf2.value === 'string') {
              // For sprite frames, use nearest
              return t < 0.5 ? kf1.value : kf2.value;
            }

            // Numeric interpolation with easing
            const easedT = applyEasing(t, kf1.easing, kf1.bezierHandles);
            return (kf1.value as number) + ((kf2.value as number) - (kf1.value as number)) * easedT;
          }
        }

        return undefined;
      },

      // Project state methods
      loadProjectState: (projectState: {
        animations?: Record<string, Animation>;
        selectedAnimationId?: string | null;
        editorPreferences?: {
          snapToFrames?: boolean;
          autoKeyframe?: boolean;
          showGrid?: boolean;
          gridSubdivisions?: number;
          onionSkinEnabled?: boolean;
        };
      }) => {
        set((draft) => {
          draft.animations = projectState.animations || {};
          draft.selectedAnimationId = projectState.selectedAnimationId ?? null;
          if (projectState.editorPreferences) {
            draft.snapToFrames = projectState.editorPreferences.snapToFrames ?? true;
            draft.autoKeyframe = projectState.editorPreferences.autoKeyframe ?? false;
            draft.showGrid = projectState.editorPreferences.showGrid ?? true;
            draft.gridSubdivisions = projectState.editorPreferences.gridSubdivisions ?? 4;
            draft.onionSkin.enabled = projectState.editorPreferences.onionSkinEnabled ?? false;
          }
        });
      },

      getProjectState: () => {
        const state = get();
        return {
          version: 1,
          lastModified: Date.now(),
          animations: state.animations || {},
          selectedAnimationId: state.selectedAnimationId,
          editorPreferences: {
            snapToFrames: state.snapToFrames ?? true,
            autoKeyframe: state.autoKeyframe ?? false,
            showGrid: state.showGrid ?? true,
            gridSubdivisions: state.gridSubdivisions ?? 4,
            onionSkinEnabled: state.onionSkin?.enabled ?? false,
          },
        };
      },

      resetProjectState: () => {
        set((draft) => {
          draft.animations = {};
          draft.selectedAnimationId = null;
          draft.selectedTrackIds = [];
          draft.selectedKeyframeIds = [];
          draft.playback.currentTime = 0;
          draft.playback.currentFrame = 0;
          draft.playback.isPlaying = false;
          draft.playback.isPaused = false;
        });
      },
    }))
);
